import { useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./primitives.module.css";
import { cn } from "../../utils/cn";
import TRAText from "./TRAText";
import { useFocusTrap } from "../../utils/useFocusTrap";

export function TRAModal({ open, onClose, title, children, ariaLabel, titleAlign = "center" }) {
  const dialogRef = useRef(null);
  useFocusTrap({
    enabled: Boolean(open),
    containerRef: dialogRef,
    onEscape: onClose,
  });

  if (!open) return null;
  return createPortal(
    <div className={styles.modalViewport}>
      <div className={styles.overlay} onClick={onClose} aria-hidden />
      <div className={styles.modalShell}>
        <div
          ref={dialogRef}
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel || title}
        >
          {title ? (
            <TRAText
              as="h2"
              variant="h2"
              className={cn(
                styles.modalTitle,
                titleAlign === "center" && styles.modalTitleCenter
              )}
            >
              {title}
            </TRAText>
          ) : null}
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function TRADrawer({ open, onClose, children, side = "bottom", ariaLabel, className }) {
  const dialogRef = useRef(null);
  useFocusTrap({
    enabled: Boolean(open),
    containerRef: dialogRef,
    onEscape: onClose,
  });

  if (!open) return null;
  return createPortal(
    <>
      {onClose ? <div className={styles.overlay} onClick={onClose} aria-hidden /> : null}
      <div
        ref={dialogRef}
        className={cn(side === "bottom" ? styles.drawerBottom : styles.modal, className)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

/**
 * Ephemeral status toast (portal).
 * @param {"bottom"|"center"} [placement="bottom"] — center for mid-screen session cues (e.g. Go Live).
 */
export function TRAToast({ message, visible, placement = "bottom" }) {
  if (!visible || !message) return null;
  return createPortal(
    <div
      className={cn(styles.toast, placement === "center" && styles.toastCenter)}
      role="status"
      aria-live="polite"
    >
      {message}
    </div>,
    document.body
  );
}

export function TRATooltip({ children, label }) {
  return (
    <span title={label}>
      <span className="tra-sr-only">{label}</span>
      <span aria-hidden>{children}</span>
    </span>
  );
}
